import React from 'react';
import { List, ListItemButton, Tooltip } from '@mui/material';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { useLoggedInUserName } from 'src/hooks/useLoggedInUserName';
import {
  isPregnancyNode,
  isGameteDonorNode,
  canCarryPregnancy
} from '../../pedigree/config';
import {
  PregnancyModal,
  DeleteConfirm,
  AddFetusConfirm,
  usePregnancyCrud,
  spouseOptionsFor
} from './pregnancyEditor';
import {
  quickAddPregnancyPayload,
  appendFetusPayload
} from '../../pedigree/pregnancyQuickAdd';

// Track B (pregnancy-record model): pregnancies + donors are RECORDS on the carrier
// (Pregnancy/PregnancyChanged), not persons or config nodes; the pedigree DERIVES the
// render nodes from them. This context-menu entry authors those records via the shared
// editor (PregnancyModal + usePregnancyCrud). Add lives on a carrier; Edit/Delete resolve
// the carrier + record behind a DERIVED pregnancy/donor node via its uid (pregnancy uid =
// record._id; donor uid = `<record._id>:<index>`).

// The record _id behind a derived node (pregnancy uid = _id; donor uid = `_id:index`).
const recordIdOf = (node) => {
  const uid = node?.uid == null ? '' : String(node.uid);
  return isGameteDonorNode(node) ? uid.split(':')[0] : uid;
};

// Resolve the carrier MEMBER (from memberRecords, the source of truth) + the pregnancy
// record for a node. For a carrier node → that member + no record (Add). For a derived
// pregnancy/donor node → the member whose pregnancies[] holds the record + the record.
const resolveCarrierAndRecord = (node, memberRecords) => {
  const list = memberRecords || [];
  if (isPregnancyNode(node) || isGameteDonorNode(node)) {
    const rid = recordIdOf(node);
    const carrier = list.find((m) =>
      (m.pregnancies || []).some((p) => String(p._id) === rid)
    );
    const record = carrier?.pregnancies?.find((p) => String(p._id) === rid);
    return { carrier, record };
  }
  const carrier = list.find((m) => m.pid === node.pid) || node;
  return { carrier, record: null };
};

export default function ReproductiveMenu({
  node,
  user,
  memberRecords,
  setMemberRecords,
  handleClose,
  setPedigreeState
}) {
  const { setSnack } = React.useContext(SnackContext);
  // See ReproductiveSection: the family's diagnoses drive colour consistency for a fetus diagnosis.
  const { content } = React.useContext(AppContext);
  const familyDiagnoses = content?.pedigreeSelectedUsers?.diagnoses || [];
  // Attribution for fetus diagnoses/phenotypes — the signed-in clinician.
  const recordedBy = useLoggedInUserName();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [fetusConfirmOpen, setFetusConfirmOpen] = React.useState(false);
  const {
    busy,
    submitPregnancy,
    deletePregnancy,
    toggleHidden,
    convertToLiveBirth
  } = usePregnancyCrud({
    memberRecords,
    setMemberRecords,
    setPedigreeState,
    setSnack,
    user
  });

  const { carrier, record } = resolveCarrierAndRecord(node, memberRecords);
  const isReproNodeSelected = isPregnancyNode(node) || isGameteDonorNode(node);
  const canCarry = canCarryPregnancy(node);
  const spouseOptions = spouseOptionsFor(carrier, memberRecords);

  // Return the result; the modal closes itself via onClose (and may first prompt to add the
  // live-birth child).
  const handleSubmit = (formValues) =>
    submitPregnancy(carrier, record, formValues);

  const closeAll = () => {
    setModalOpen(false);
    handleClose?.();
  };

  const handleDelete = async () => {
    const ok = await deletePregnancy(carrier, record);
    if (ok) {
      setConfirmOpen(false);
      handleClose?.();
    }
  };

  // The one-click path: no form, just the confirmation and the record. Since TGCLOUD-1617 item 2 this
  // is reachable ONLY from an existing pregnancy's menu ("Add another fetus"), where it appends a
  // second fetus and the twin machinery draws the pair. The carrier's menu now opens the form instead,
  // so the `quickAddPregnancyPayload` branch below is a defensive fallback rather than a live path.
  const handleQuickAddFetus = async () => {
    const payload = record
      ? appendFetusPayload(record)
      : quickAddPregnancyPayload(new Date());
    const saved = await submitPregnancy(carrier, record, payload);
    if (saved) {
      setFetusConfirmOpen(false);
      handleClose?.();
    }
  };

  // A visible repro node can be hidden from here; it then can't be right-clicked, so
  // un-hiding is done from the carrier's Reproductive sidebar (which lists hidden records).
  const handleHide = async () => {
    const ok = await toggleHidden(carrier, record);
    if (ok) handleClose?.();
  };

  return (
    <>
      <List>
        {isReproNodeSelected ? (
          <>
            <ListItemButton onClick={() => setModalOpen(true)}>
              Edit pregnancy…
            </ListItemButton>
            {/* Only on a pregnancy — a donor node has no fetus list of its own. */}
            {isPregnancyNode(node) && (
              <ListItemButton
                onClick={() => setFetusConfirmOpen(true)}
                disabled={busy}
              >
                Add another fetus
              </ListItemButton>
            )}
            <ListItemButton onClick={handleHide} disabled={busy}>
              Hide from pedigree
            </ListItemButton>
            <ListItemButton onClick={() => setConfirmOpen(true)}>
              Delete pregnancy
            </ListItemButton>
          </>
        ) : (
          <Tooltip
            title={
              canCarry
                ? ''
                : 'Set Sex Assigned at Birth to Female before adding a pregnancy'
            }
            placement="right"
          >
            <span style={{ display: 'block' }}>
              {/* ONE way in (TGCLOUD-1617 item 2). Kathryn: "it doesn't make sense to have both
                  options… I would prefer to have it say add fetus. But I want the functionality that
                  we've got for add pregnancy." So the label is hers and the behaviour is the FORM —
                  which is also what gives her the partner default she asked for (item 3): a new
                  record seeds "conceived with" from the carrier's spouse.

                  The one-click quick-add is gone from this menu with it. That deliberately reverses
                  an earlier choice of mine — the quick-add asserted nothing about conception so that
                  nothing was assumed — because her workflow is overwhelmingly partnered, not donor.
                  "Add another fetus" survives on an existing pregnancy's own menu (Tim's decision 1),
                  since that is the only way to add a twin from the chart. */}
              <ListItemButton
                onClick={() => setModalOpen(true)}
                disabled={!canCarry}
              >
                Add fetus…
              </ListItemButton>
            </span>
          </Tooltip>
        )}
      </List>

      {modalOpen && (
        <PregnancyModal
          record={record}
          spouseOptions={spouseOptions}
          familyDiagnoses={familyDiagnoses}
          recordedBy={recordedBy}
          carrier={carrier}
          onSubmit={handleSubmit}
          onClose={closeAll}
          onConvert={(rec, gender, fetusIndex) =>
            convertToLiveBirth(carrier, rec, gender, fetusIndex)
          }
        />
      )}
      {fetusConfirmOpen && (
        <AddFetusConfirm
          existing={!!record}
          onConfirm={handleQuickAddFetus}
          onClose={() => setFetusConfirmOpen(false)}
          saving={busy}
        />
      )}
      {confirmOpen && (
        <DeleteConfirm
          onConfirm={handleDelete}
          onClose={() => setConfirmOpen(false)}
          deleting={busy}
        />
      )}
    </>
  );
}
